import { getTreatAsRemoved } from './TimesheetDetail';
import _ from 'lodash-es';
import moment from 'moment';

export const getUpdatedJobTime = (timesheet, eventValue) => {
    const jobDate = moment(timesheet.jobDate);

    const splitEventValues = eventValue.split(':');

    return jobDate
        .startOf('day')
        .add(splitEventValues[0], 'hour')
        .add(splitEventValues[1], 'minute')
        .toDate();
};

const createStandaloneAdjustment = (job, contractPhaseId, timesheet) => {
    const contractPhase = job.contract.contractPhases.find(
        (cp) => cp.id == contractPhaseId
    );

    const timesheetPhaseMaterialAdjustments = Array.from(
        contractPhase.phaseMaterials.map((pm) => {
            return {
                phaseMaterialId: pm.id,
                received: 0,
                quantity: 0,
            };
        })
    );

    timesheet.timesheetContractPhaseAdjustments.push({
        contractPhaseId: contractPhaseId,
        quantity: 0,
        timeSpent: 0,
        timesheetId: timesheet.id,
        timesheetPhaseMaterialAdjustments,
    });

    timesheet.timesheetDetails.forEach((td) => {
        if (
            td.timesheetDetailContractPhaseAdjustments.some(
                (tdcpa) => tdcpa.contractPhaseId == contractPhaseId
            )
        ) {
            return;
        }

        td.timesheetDetailContractPhaseAdjustments.push({
            timesheetDetailId: td.id,
            contractPhaseId: contractPhaseId,
            quantity: 0,
        });
    });
};

const cloneContractPhaseAsAdjustment = (
    timesheetContractPhase,
    timesheet,
    contractPhaseId
) => {
    const timesheetPhaseMaterialAdjustments = _.cloneDeep(
        timesheetContractPhase.timesheetPhaseMaterials
    );

    timesheetPhaseMaterialAdjustments.forEach((tpma) => {
        tpma.id = 0;
    });

    const timesheetContractPhaseAdjustment = {
        ...timesheetContractPhase,
        timesheetPhaseMaterialAdjustments,
    };

    timesheetContractPhase.clientAdjustment = true;

    timesheetContractPhaseAdjustment.id = 0;
    timesheetContractPhaseAdjustment.timesheetContractPhaseId =
        timesheetContractPhase.id;
    timesheetContractPhaseAdjustment.clientAdjustment = true;

    if (!timesheetContractPhaseAdjustment.quantity) {
        timesheetContractPhaseAdjustment.quantity = 0;
    }

    let timeSpent = 0;

    timesheet.timesheetDetails
        .filter((td) => !getTreatAsRemoved(td))
        .forEach((td) => {
            td.timesheetDetailContractPhaseAdjustments
                .filter(
                    (tdcpa) =>
                        tdcpa.contractPhaseId == contractPhaseId &&
                        !isNaN(tdcpa.quantity)
                )
                .forEach((tdcpa) => {
                    timeSpent += tdcpa.quantity;
                });

            td.timesheetDetailContractPhases
                .filter(
                    (tdcp) =>
                        tdcp.contractPhaseId == contractPhaseId &&
                        !tdcp.clientAdjustment &&
                        !tdcp.timesheetDetailContractPhaseAdjustmentId
                )
                .forEach((tdcp) => {
                    timeSpent += tdcp.quantity;
                });
        });

    timesheetContractPhaseAdjustment.timeSpent = timeSpent;

    timesheet.timesheetContractPhaseAdjustments.push(
        timesheetContractPhaseAdjustment
    );
};

/**
 * At worst, this will mutate job with a new timesheet contract phase adjustment.
 * At best, it will see that an adjustment for that contract phase exists and do nothing.
 * @param {*} timesheet
 * @param {*} job
 * @param {*} contractPhaseId
 * @returns
 */
export const addClientTimesheetContractPhaseAdjustmentSafe = (
    timesheet,
    job,
    contractPhaseId
) => {
    const existingAdjustment = timesheet.timesheetContractPhaseAdjustments.find(
        (tcpa) => tcpa.contractPhaseId == contractPhaseId
    );

    if (existingAdjustment) {
        return;
    }

    const timesheetContractPhase = timesheet.timesheetContractPhases.find(
        (tcp) => tcp.contractPhaseId == contractPhaseId
    );

    if (!timesheetContractPhase) {
        createStandaloneAdjustment(job, contractPhaseId, timesheet);

        return;
    }

    cloneContractPhaseAsAdjustment(
        timesheetContractPhase,
        timesheet,
        contractPhaseId
    );
};

const addClientTimesheetDetailContractPhaseAdjustment = (
    td,
    contractPhaseId,
    quantity
) => {
    const timesheetDetailContractPhase = td.timesheetDetailContractPhases.find(
        (td) => td.contractPhaseId == contractPhaseId
    );

    const timesheetDetailContractPhaseAdjustment = {
        ...timesheetDetailContractPhase,
    };

    timesheetDetailContractPhase.clientAdjustment = true;

    timesheetDetailContractPhaseAdjustment.id = 0;
    timesheetDetailContractPhaseAdjustment.timesheetDetailContractPhaseId =
        timesheetDetailContractPhase.id;
    timesheetDetailContractPhaseAdjustment.quantity = quantity ?? 0;
    timesheetDetailContractPhaseAdjustment.clientAdjustment = true;
    timesheetDetailContractPhaseAdjustment.targetQuantity =
        timesheetDetailContractPhase.targetQuantity;

    td.timesheetDetailContractPhaseAdjustments.push(
        timesheetDetailContractPhaseAdjustment
    );
};

export const createTimesheetDetailContractPhaseAdjustment = (
    timesheet,
    timesheetDetailId,
    contractPhaseId,
    quantity
) => {
    quantity = quantity || 0;

    timesheet.timesheetDetails.forEach((td) => {
        if (td.id != timesheetDetailId) {
            return;
        }

        let detailsAdjusted = false;

        td.timesheetDetailContractPhases.forEach((tdcp) => {
            if (tdcp.contractPhaseId != contractPhaseId) {
                return;
            }

            tdcp.clientAdjustment = true;
        });

        td.timesheetDetailContractPhaseAdjustments.forEach((tdcpa) => {
            if (tdcpa.contractPhaseId != contractPhaseId) {
                return;
            }

            tdcpa.quantity = quantity;
            detailsAdjusted = true;
        });

        if (!detailsAdjusted) {
            addClientTimesheetDetailContractPhaseAdjustment(
                td,
                contractPhaseId,
                quantity
            );
        }
    });
};

export const areTimesSet = (timesheet) =>
    Boolean(
        timesheet &&
        ((timesheet.startTime && timesheet.endTime) ||
            (timesheet.adjustedStartTime && timesheet.adjustedEndTime))
    );

export const hasAnyAdjustmentData = (timesheet) => {
    if (!timesheet) {
        return false;
    }

    if (timesheet.adjustedStartTime || timesheet.adjustedEndTime) {
        return true;
    }

    if (timesheet.timesheetContractPhaseAdjustments.length > 0) {
        return true;
    }

    if (
        timesheet.timesheetDetails.some(
            (td) =>
                td.adjustedStart ||
                td.adjustedEnd ||
                td.adjustedRemoved != null ||
                td.adjustmentNotes ||
                td.timesheetDetailContractPhaseAdjustments.length > 1
        )
    ) {
        return true;
    }

    return false;
};

export const isNoQuantityAllowed = (timesheetContractPhase) =>
    timesheetContractPhase && timesheetContractPhase.targetQuantity === 0;

